// src/features/agreement/components/minutaPreview.tsx
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ChevronLeft, CheckCircle, XCircle, Clock } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { getCleanDomain } from '@/utils/getCleanDomain'
import ButtonConfirm from '@/components/ui/button-confirm'
import { useFetchPreviewAgreementById } from '../services/useFetchPreviewAgreementById'
import { useRejectEditAgreement } from '../services/useRejectEditAgreement'
import { useApprovedEditAgreement } from '../services/useApprovedEditAgreement'
import { useCurrentUser } from '@/features/auth/state/auth'
import { getDomainIcon } from '../utils/domainIcon'

export default function EditPreview() {
  const { editId } = useParams<{ editId: string }>()
  const navigate = useNavigate()
  const user = useCurrentUser()

  const [showHighlights, setShowHighlights] = useState(true)

  const domain = getCleanDomain()
  const selectedIcon = getDomainIcon(domain)

  // busca do preview (nunca condicionar a chamada do hook com if)
  const { data, isLoading, isError, error, refetch } = useFetchPreviewAgreementById(editId || '', showHighlights)

  // mutations declaradas SEMPRE
  const { mutate: approveEdit } = useApprovedEditAgreement()
  const { mutate: rejectEdit } = useRejectEditAgreement()

  // estados locais SEMPRE
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // derive dados com fallback seguro
  const previewData = data as
    | {
        preview: string
        edit: { id: string; requestChange: string; createdAt: string; isEdit: boolean; acceptEdit: boolean | null }
        agreement: { id: string; htmlContent: string }
      }
    | undefined

  const status = previewData?.edit?.acceptEdit ?? null

  // useMemo CHAMADO em todos os renders (usa fallback)
  const statusConfig = useMemo(() => {
    if (status === true) {
      return {
        label: 'Aprovada',
        color: 'bg-green-50 text-green-700 border-green-200',
        icon: <CheckCircle className="w-4 h-4" />
      }
    }
    if (status === false) {
      return {
        label: 'Rejeitada',
        color: 'bg-red-50 text-red-700 border-red-200',
        icon: <XCircle className="w-4 h-4" />
      }
    }
    return {
      label: 'Em Análise',
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      icon: <Clock className="w-4 h-4" />
    }
  }, [status])

  const createdDate =
    previewData?.edit?.createdAt &&
    new Date(previewData.edit.createdAt).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })

  // ------------- Depois dos hooks, agora sim retornos condicionais -------------
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-slate-600">Carregando pré-visualização…</div>
      </div>
    )
  }

  if (isError || !previewData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Alert className="max-w-md border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {String((error as any)?.response?.data?.message ?? 'Dados de pré-visualização não encontrados')}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // ------------- Handlers (sem hooks aqui dentro) -------------
  function approve() {
    approveEdit(previewData?.edit.id, {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Alteração aprovada com sucesso.' })
        refetch()
      },
      onError: () => setMessage({ type: 'error', text: 'Falha ao aprovar. Tente novamente.' })
    })
  }

  function reject() {
    rejectEdit(previewData?.edit.id, {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Alteração rejeitada.' })
        refetch()
      },
      onError: () => setMessage({ type: 'error', text: 'Falha ao rejeitar. Tente novamente.' })
    })
  }

  // ------------- Render -------------
  return (
    <div>
      <style>{`
        /* Inserções de texto */
        .diff-ins {
          background: #d1fae5;
          border-bottom: 2px solid #10b981;
          text-decoration: none;
        }
        /* Remoções de texto */
        .diff-del {
          background: #fee2e2;
          text-decoration: line-through;
        }
        /* Marcador para blocos removidos (tags inteiras) */
        .diff-del-block {
          background: #fecaca;
          color: #7f1d1d;
          border-radius: 10px;
          padding: 0 6px;
          font-size: 12px;
          white-space: nowrap;
        }
      `}</style>
      {/* logo acima do bloco do preview */}

      <Card className="shadow-sm border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
        <div className="pt-6 px-4">
          <div className="flex flex-col items-center text-center gap-1">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight">
              Pré-Visualização da Minuta
            </h1>
            <p className="text-sm md:text-base text-slate-600">Visualize as alterações propostas no contrato</p>
          </div>
        </div>

        <div className="sticky top-0 z-10 bg-white/70 backdrop-blur border-b">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate(-1)} className="h-9">
                <ChevronLeft className="w-4 h-4" />
                Voltar
              </Button>
              <Separator orientation="vertical" className="h-5" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Solicitação #{previewData.edit.id}</span>
                  <Badge className={cn('border', statusConfig.color)}>
                    <span className="flex items-center gap-1">
                      {statusConfig.icon}
                      {statusConfig.label}
                    </span>
                  </Badge>
                </div>
                <div className="text-xs text-slate-500">Criada em {createdDate}</div>
              </div>
              <Button
                variant={showHighlights ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowHighlights(v => !v)}
              >
                {showHighlights ? 'Ocultar destaques' : 'Mostrar destaques'}
              </Button>
            </div>
            {status === null && user?.typeCargo === 'CEO' && (
              <div className="flex items-center gap-2">
                <ButtonConfirm
                  triggerText="Rejeitar"
                  triggerVariant="destructive"
                  triggerSize="sm"
                  confirmText="Tem certeza que deseja rejeitar esta alteração?"
                  confirmBtnText="Rejeitar"
                  confirmBtnVariant="destructive"
                  cancelBtnVariant="outline"
                  onConfirm={reject}
                />

                <ButtonConfirm
                  triggerText="Aceitar"
                  triggerVariant="default"
                  triggerSize="sm"
                  confirmText="Deseja aplicar esta alteração ao contrato?"
                  confirmBtnText="Confirmar"
                  confirmBtnVariant="default"
                  onConfirm={approve}
                />
              </div>
            )}
          </div>
        </div>

        {message && (
          <div className="px-4 pt-3">
            <Alert
              className={cn(
                'border',
                message.type === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              )}
            >
              <AlertDescription className={cn(message.type === 'success' ? 'text-green-800' : 'text-red-800')}>
                {message.text}
              </AlertDescription>
            </Alert>
          </div>
        )}

        <CardContent className="p-0">
          <div
            className="absolute inset-0 pointer-events-none z-0"
            style={{
              backgroundImage: `url(${selectedIcon})`,
              backgroundRepeat: 'repeat',
              backgroundSize: '100%',
              opacity: 0.08
            }}
          />

          <div className="relative z-10 p-6">
            <div className="max-h-[calc(100vh-220px)] overflow-y-auto rounded-lg border bg-white/60 backdrop-blur p-4">
              <div
                className="contract-display prose max-w-none"
                dangerouslySetInnerHTML={{ __html: previewData.preview }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
